/**
 * Golf Shots Video Recorder
 * - Hold to record, release to stop
 * - Lens filters: Normal, Fisheye, Old Movie
 * - Use or Retake after recording
 * - Outputs MP4 (H.264) for best quality/size
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import { 
  Camera, 
  Upload, 
  Scissors,
  Loader2,
  X,
  Play,
  Pause,
  RefreshCw,
  RefreshCcw,
  CheckCircle,
  ChevronLeft,
  RotateCw,
  Film,
  Circle,
  Eye,
  Sparkles,
  Volume2,
  VolumeX,
  FlipHorizontal,
  SwitchCamera
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX_VIDEO_SECONDS = 15;  // Allow 15 second recordings for MP4
const MIN_CLIP_DURATION = 2;
const MAX_CLIP_DURATION = 15;  // Allow up to 15 second clips

// Beauty/Enhancement filter options - makes users look clean and fresh
const LENS_FILTERS = {
  none: { 
    name: 'None', 
    filter: 'none', 
    icon: Eye,
    ffmpeg: null,
    color: '#888'
  },
  bright: { 
    name: 'Bright', 
    filter: 'brightness(135%) contrast(115%) saturate(110%)', 
    icon: Sparkles,
    ffmpeg: 'eq=brightness=0.15:contrast=1.15:saturation=1.1',
    color: '#fbbf24' // Yellow
  },
  warm: { 
    name: 'Warm', 
    filter: 'sepia(35%) saturate(140%) brightness(110%) contrast(105%)', 
    icon: Sparkles,
    ffmpeg: 'colortemperature=temperature=4500,eq=saturation=1.4:brightness=0.08',
    color: '#f97316' // Orange
  },
  fresh: { 
    name: 'Fresh', 
    filter: 'saturate(150%) brightness(115%) contrast(110%) hue-rotate(10deg)', 
    icon: Sparkles,
    ffmpeg: 'eq=saturation=1.5:brightness=0.1:contrast=1.1',
    color: '#22c55e' // Green
  },
  cool: { 
    name: 'Cool', 
    filter: 'saturate(85%) brightness(110%) contrast(110%) hue-rotate(-15deg) sepia(5%)', 
    icon: Sparkles,
    ffmpeg: 'colorbalance=rs=-0.15:gs=0:bs=0.15,eq=brightness=0.08:contrast=1.1',
    color: '#3b82f6' // Blue
  },
  glow: { 
    name: 'Glow', 
    filter: 'brightness(120%) contrast(90%) saturate(130%) blur(0.3px)', 
    icon: Sparkles,
    ffmpeg: 'eq=brightness=0.12:contrast=0.9:saturation=1.3',
    color: '#ec4899' // Pink
  },
  dramatic: { 
    name: 'Drama', 
    filter: 'contrast(140%) brightness(95%) saturate(120%)', 
    icon: Sparkles,
    ffmpeg: 'eq=contrast=1.4:brightness=-0.03:saturation=1.2',
    color: '#8b5cf6' // Purple
  },
  vintage: { 
    name: 'Vintage', 
    filter: 'sepia(50%) contrast(95%) brightness(105%) saturate(80%)', 
    icon: Sparkles,
    ffmpeg: 'colorchannelmixer=.393:.769:.189:0:.349:.686:.168:0:.272:.534:.131,eq=brightness=0.03',
    color: '#a3a3a3' // Gray
  }
};

const GolfShotsModal = ({ isOpen, onClose, onGifCreated }) => {
  const [step, setStep] = useState(1); // 1=upload, 1.5=review, 2=select, 3=done
  
  // Video
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraPreview, setCameraPreview] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false); // Loading state for camera
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false); // Track if permission was denied
  const [isSelfieMode, setIsSelfieMode] = useState(false); // Front camera toggle
  const [captureMode, setCaptureMode] = useState('video'); // 'photo' or 'video'
  const [zoomLevel, setZoomLevel] = useState(1); // Zoom level: 0.5, 1, 1.5, 2
  
  // Lens filter
  const [selectedLens, setSelectedLens] = useState('none');
  
  // Review state (after recording, before trim)
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null); // For photo mode
  
  // Trim
  const [clipStart, setClipStart] = useState(0);
  const [clipDuration, setClipDuration] = useState(3); // Default to 3s (minimum)
  const [rotation, setRotation] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null); // 'left', 'right', or 'center'
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartValues, setDragStartValues] = useState({ start: 0, duration: 3 });
  const [includeSound, setIncludeSound] = useState(true); // Sound on by default
  
  // Output
  const [isCreating, setIsCreating] = useState(false);
  const [outputUrl, setOutputUrl] = useState(null);
  
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const scrubberRef = useRef(null);
  const loopIntervalRef = useRef(null);
  const recordButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) resetAll();
  }, [isOpen]);

  // Handle video loop within clip bounds
  useEffect(() => {
    if (!videoRef.current || step !== 2 || !isPlaying) return;
    
    const video = videoRef.current;
    const clipEnd = Math.min(clipStart + clipDuration, videoDuration);
    
    const checkLoop = () => {
      if (video.currentTime >= clipEnd - 0.1) {
        video.currentTime = clipStart;
      }
    };
    
    loopIntervalRef.current = setInterval(checkLoop, 100);
    return () => clearInterval(loopIntervalRef.current);
  }, [step, clipStart, clipDuration, videoDuration, isPlaying]);

  // Update video muted state when sound toggle changes
  useEffect(() => {
    if (videoRef.current && step === 2) {
      videoRef.current.muted = !includeSound;
    }
  }, [includeSound, step]);

  const resetAll = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (pendingStreamRef.current) pendingStreamRef.current.getTracks().forEach(t => t.stop());
    if (recordingIntervalRef.current) cancelAnimationFrame(recordingIntervalRef.current);
    if (loopIntervalRef.current) clearInterval(loopIntervalRef.current);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    
    pendingStreamRef.current = null;
    setStep(1);
    setVideoFile(null);
    setVideoUrl(null);
    setVideoDuration(0);
    setClipStart(0);
    setClipDuration(3); // Default to 3s (minimum)
    setRotation(0);
    setIsPlaying(false);
    setIsRecording(false);
    setRecordingTime(0);
    setIsCreating(false);
    setOutputUrl(null);
    setIsDragging(false);
    setDragHandle(null);
    setCameraPreview(false);
    setCameraLoading(false);
    setCameraPermissionDenied(false);
    setSelectedLens('none');
    setRecordedBlob(null);
    setCapturedPhoto(null);
    setCaptureMode('video');
    setZoomLevel(1);
  };
  
  // Apply zoom to camera stream
  const applyZoom = async (newZoom) => {
    setZoomLevel(newZoom);
    
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities();
          if (capabilities.zoom) {
            const minZoom = capabilities.zoom.min || 1;
            const maxZoom = capabilities.zoom.max || 1;
            const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
            await videoTrack.applyConstraints({ advanced: [{ zoom: clampedZoom }] });
          }
        } catch (e) {
          console.log('Zoom not supported:', e);
        }
      }
    }
  };
  
  // Rotate video 90 degrees clockwise
  const rotateVideo = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Handle image upload
    if (file.type.startsWith('image/')) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error("Max 20MB for images");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedPhoto(e.target.result);
        setCaptureMode('photo');
        setStep(1.5); // Go to review
      };
      reader.readAsDataURL(file);
      return;
    }
    
    // Handle video upload
    if (!file.type.startsWith('video/')) {
      toast.error("Select a video or image file");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Max 50MB for videos");
      return;
    }
    setVideoFile(file);
    setVideoUrl(URL.createObjectURL(file));
    setCaptureMode('video');
    setStep(2);
  };

  // Ref to store pending stream while waiting for video element
  const pendingStreamRef = useRef(null);
  
  // Callback ref to handle video element mounting
  const videoRefCallback = useCallback((node) => {
    if (node) {
      videoRef.current = node;
      // If we have a pending stream, attach it now
      if (pendingStreamRef.current) {
        attachStreamToVideo(node, pendingStreamRef.current);
      }
    }
  }, []);
  
  // Function to attach stream to video element
  const attachStreamToVideo = async (videoElement, stream) => {
    if (!videoElement || !stream) return;
    
    try {
      console.log('Attaching stream to video element...');
      videoElement.srcObject = stream;
      videoElement.muted = true;
      
      // iOS Safari needs these attributes
      videoElement.setAttribute('autoplay', '');
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('webkit-playsinline', 'true');
      
      // Try to play with retry logic for iOS
      let playAttempts = 0;
      const tryPlay = async () => {
        try {
          await videoElement.play();
          console.log('Video playing successfully');
          pendingStreamRef.current = null;
          setCameraLoading(false);
        } catch (e) {
          console.log('Play attempt failed:', e, 'Retrying...');
          playAttempts++;
          if (playAttempts < 5) {
            setTimeout(tryPlay, 200);
          } else {
            console.error('Failed to play video after retries');
            setCameraLoading(false);
          }
        }
      };
      
      await tryPlay();
    } catch (e) {
      console.error('Error attaching stream:', e);
      setCameraLoading(false);
    }
  };
  
  // Check camera permission status
  const checkCameraPermission = async () => {
    try {
      // Try to check permission state first (supported in modern browsers)
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'camera' });
        return result.state; // 'granted', 'denied', 'prompt'
      }
      return 'prompt'; // Assume prompt if API not available
    } catch (e) {
      return 'prompt';
    }
  };
  
  // Open camera preview (without recording)
  const openCameraPreview = async () => {
    setCameraLoading(true);
    setCameraPermissionDenied(false);
    
    try {
      // Check if camera API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not available');
      }
      
      // iOS Safari requires specific constraints
      const constraints = { 
        video: { 
          facingMode: isSelfieMode ? 'user' : { ideal: 'environment' },
          width: { ideal: 1280 }, 
          height: { ideal: 720 }
        }, 
        audio: true  // Enable audio recording for videos with sound
      };
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        console.log('Trying fallback constraints:', e);
        // Try simpler constraints as fallback (still with audio)
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch (e2) {
          // If audio fails, try video only as last resort
          console.log('Audio not available, trying video only:', e2);
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          toast.info("Recording without audio - microphone not available");
        }
      }
      
      console.log('Got camera stream:', stream.id);
      streamRef.current = stream;
      pendingStreamRef.current = stream;
      
      // NOW set cameraPreview to true to trigger render of video element
      setCameraPreview(true);
      
      // If video element already exists (from previous use), attach immediately
      if (videoRef.current) {
        attachStreamToVideo(videoRef.current, stream);
      }
      // Otherwise, the videoRefCallback will handle it when the element mounts
      
    } catch (err) {
      console.error('Camera error:', err);
      setCameraPreview(false);
      setCameraLoading(false);
      setCameraPermissionDenied(true);
      
      // Provide specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error("Camera permission denied. Please enable camera access in your browser settings.");
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        toast.error("No camera found on this device.");
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        toast.error("Camera is in use by another application.");
      } else {
        toast.error("Unable to access camera. Please check permissions.");
      }
    }
  };
  
  // Start actual recording (after preview is open)
  const startRecording = async () => {
    if (!streamRef.current) return;
    
    try {
      // Check for supported MIME types
      let mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported('video/webm')) {
        if (MediaRecorder.isTypeSupported('video/mp4')) {
          mimeType = 'video/mp4';
        } else if (MediaRecorder.isTypeSupported('video/quicktime')) {
          mimeType = 'video/quicktime';
        }
      }
      
      const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        
        // Store the recorded blob for review
        setRecordedBlob(blob);
        const videoObjectUrl = URL.createObjectURL(blob);
        setVideoUrl(videoObjectUrl);
        
        // Auto-detect rotation needed based on video dimensions
        autoDetectRotation(videoObjectUrl);
        
        // Stop camera but don't close preview yet - go to review
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
        
        // Go to review step (1.5)
        setStep(1.5);
        setCameraPreview(false);
        if (recordingIntervalRef.current) cancelAnimationFrame(recordingIntervalRef.current);
      };

      setRecordingTime(0);
      const startTime = Date.now();
      const updateTimer = () => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingTime(Math.min(elapsed, MAX_VIDEO_SECONDS));
        
        if (elapsed >= MAX_VIDEO_SECONDS) {
          mediaRecorderRef.current?.stop();
          setIsRecording(false);
        } else if (mediaRecorderRef.current?.state === 'recording') {
          recordingIntervalRef.current = requestAnimationFrame(updateTimer);
        }
      };
      recordingIntervalRef.current = requestAnimationFrame(updateTimer);

      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (err) {
      console.error('Recording error:', err);
      toast.error("Failed to start recording");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        cancelAnimationFrame(recordingIntervalRef.current);
      }
    }
  };

  // Capture a photo from the video stream
  const capturePhoto = () => {
    if (!videoRef.current || !streamRef.current) {
      toast.error("Camera not ready");
      return;
    }
    
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      
      const ctx = canvas.getContext('2d');
      
      // If selfie mode, flip horizontally
      if (isSelfieMode) {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      
      // Draw the video frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Apply the selected filter by drawing to another canvas with filter
      const filterCanvas = document.createElement('canvas');
      filterCanvas.width = canvas.width;
      filterCanvas.height = canvas.height;
      const filterCtx = filterCanvas.getContext('2d');
      filterCtx.filter = LENS_FILTERS[selectedLens]?.filter || 'none';
      filterCtx.drawImage(canvas, 0, 0);
      
      // Get the photo as data URL
      const photoData = filterCanvas.toDataURL('image/jpeg', 0.9);
      setCapturedPhoto(photoData);
      
      // Stop the camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      setCameraPreview(false);
      setStep(1.5); // Go to review
      
      toast.success("Photo captured!");
    } catch (err) {
      console.error('Photo capture error:', err);
      toast.error("Failed to capture photo");
    }
  };
  
  // Touch event handlers for hold-to-record
  const handleRecordTouchStart = (e) => {
    e.preventDefault();
    if (!isRecording) {
      startRecording();
    }
  };
  
  const handleRecordTouchEnd = (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecording();
    }
  };
  
  // Mouse event handlers for desktop
  const handleRecordMouseDown = (e) => {
    e.preventDefault();
    if (!isRecording) {
      startRecording();
    }
  };
  
  const handleRecordMouseUp = (e) => {
    e.preventDefault();
    if (isRecording) {
      stopRecording();
    }
  };
  
  // Review handlers - Use or Retake
  const handleUseRecording = () => {
    if (recordedBlob) {
      const mimeType = recordedBlob.type;
      const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('quicktime') ? 'mov' : 'webm';
      setVideoFile(new File([recordedBlob], `golf.${ext}`, { type: mimeType }));
      
      // Initialize duration from recording time as fallback
      if (recordingTime > 0) {
        const dur = Math.min(recordingTime, MAX_VIDEO_SECONDS);
        setVideoDuration(dur);
        setClipStart(0);
        setClipDuration(Math.min(dur, MAX_CLIP_DURATION));
      }
      
      setStep(2);
    }
  };
  
  const handleRetakeRecording = () => {
    // Clear the recorded video
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setRecordedBlob(null);
    setVideoUrl(null);
    setRecordingTime(0);
    setRotation(0); // Reset rotation for new recording
    setStep(1);
    // Reopen camera
    openCameraPreview();
  };
  
  // Cancel camera preview and go back
  const cancelPreview = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraPreview(false);
    setIsRecording(false);
    setRecordingTime(0);
  };

  // Auto-detect if video needs rotation based on dimensions
  // Mobile recordings often come in with wrong orientation metadata
  const autoDetectRotation = (videoUrl) => {
    const tempVideo = document.createElement('video');
    tempVideo.muted = true;
    tempVideo.playsInline = true;
    tempVideo.setAttribute('webkit-playsinline', 'true');
    
    const detectOrientation = () => {
      const width = tempVideo.videoWidth;
      const height = tempVideo.videoHeight;
      
      console.log(`Video dimensions detected: ${width}x${height}`);
      
      if (width && height) {
        // If video is landscape (width > height) but we recorded in portrait mode
        // Auto-rotate 90 degrees to correct it
        if (width > height) {
          console.log('Auto-rotating: video appears sideways (landscape when expecting portrait)');
          setRotation(90);
          toast.success('Auto-rotated to portrait', { duration: 2000 });
        } else {
          // Video is already portrait, no rotation needed
          setRotation(0);
        }
        // Clean up
        tempVideo.src = '';
        tempVideo.load();
      }
    };
    
    tempVideo.onloadedmetadata = detectOrientation;
    
    // Also try on loadeddata as backup for iOS
    tempVideo.onloadeddata = () => {
      if (!tempVideo.videoWidth || !tempVideo.videoHeight) {
        return;
      }
      detectOrientation();
    };
    
    tempVideo.onerror = () => {
      console.log('Could not detect video dimensions for auto-rotate');
      // Default to no rotation
      setRotation(0);
    };
    
    tempVideo.src = videoUrl;
    tempVideo.load();
    
    // Force a play attempt to help iOS load metadata
    tempVideo.play().catch(() => {});
  };

  const handleVideoLoad = () => {
    const video = videoRef.current;
    if (!video) return;
    
    // Get video duration with fallback for NaN
    let dur = video.duration;
    console.log('Video duration raw:', dur);
    
    // Handle NaN or invalid duration
    if (!dur || isNaN(dur) || !isFinite(dur)) {
      // Try to get duration again after a short delay
      setTimeout(() => {
        if (video && video.duration && !isNaN(video.duration)) {
          const validDur = Math.min(video.duration, MAX_VIDEO_SECONDS);
          setVideoDuration(validDur);
          setClipStart(0);
          const initialDuration = Math.min(validDur, MAX_CLIP_DURATION);
          setClipDuration(Math.max(MIN_CLIP_DURATION, initialDuration));
          console.log('Video duration set (delayed):', validDur);
        } else {
          // Fallback to recorded time if available
          const fallbackDur = recordingTime > 0 ? recordingTime : 5;
          setVideoDuration(fallbackDur);
          setClipStart(0);
          setClipDuration(Math.min(fallbackDur, MAX_CLIP_DURATION));
          console.log('Video duration fallback:', fallbackDur);
        }
      }, 500);
      return;
    }
    
    dur = Math.min(dur, MAX_VIDEO_SECONDS);
    setVideoDuration(dur);
    
    // Set initial clip to start at 0 and use full video up to max clip duration
    setClipStart(0);
    const initialDuration = Math.min(dur, MAX_CLIP_DURATION);
    setClipDuration(Math.max(MIN_CLIP_DURATION, initialDuration));
    
    video.currentTime = 0;
    console.log('Video duration set:', dur);
  };
  
  // Additional handler for duration change events
  const handleDurationChange = () => {
    const video = videoRef.current;
    if (!video || !video.duration || isNaN(video.duration)) return;
    
    if (videoDuration === 0 || isNaN(videoDuration)) {
      const dur = Math.min(video.duration, MAX_VIDEO_SECONDS);
      setVideoDuration(dur);
      setClipStart(0);
      const initialDuration = Math.min(dur, MAX_CLIP_DURATION);
      setClipDuration(Math.max(MIN_CLIP_DURATION, initialDuration));
      console.log('Duration updated from change event:', dur);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.currentTime = clipStart;
      video.play();
      setIsPlaying(true);
    }
  };

  // Scrubber handling
  // Dual-handle trim slider
  const handleTrimStart = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    setDragHandle(handle);
    setDragStartX(clientX);
    setDragStartValues({ start: clipStart, duration: clipDuration });
    setIsDragging(true);
  };
  
  const updateTrimPosition = useCallback((e) => {
    const safeVidDuration = videoDuration > 0 && !isNaN(videoDuration) ? videoDuration : clipDuration;
    if (!scrubberRef.current || !safeVidDuration || !dragHandle) return;
    
    const clientX = e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? e.clientX;
    const rect = scrubberRef.current.getBoundingClientRect();
    const deltaX = clientX - dragStartX;
    const deltaPercent = deltaX / rect.width;
    const deltaTime = deltaPercent * safeVidDuration;
    
    if (dragHandle === 'left') {
      // Dragging left handle - changes start time and duration
      let newStart = Math.max(0, dragStartValues.start + deltaTime);
      let newDuration = dragStartValues.duration - deltaTime;
      
      // Constrain duration
      if (newDuration < MIN_CLIP_DURATION) {
        newDuration = MIN_CLIP_DURATION;
        newStart = dragStartValues.start + dragStartValues.duration - MIN_CLIP_DURATION;
      }
      if (newDuration > MAX_CLIP_DURATION) {
        newDuration = MAX_CLIP_DURATION;
        newStart = dragStartValues.start + dragStartValues.duration - MAX_CLIP_DURATION;
      }
      newStart = Math.max(0, newStart);
      
      setClipStart(newStart);
      setClipDuration(newDuration);
      if (videoRef.current) videoRef.current.currentTime = newStart;
      
    } else if (dragHandle === 'right') {
      // Dragging right handle - changes duration only
      let newDuration = Math.max(MIN_CLIP_DURATION, Math.min(MAX_CLIP_DURATION, dragStartValues.duration + deltaTime));
      
      // Don't exceed video duration
      if (clipStart + newDuration > safeVidDuration) {
        newDuration = safeVidDuration - clipStart;
      }
      newDuration = Math.max(MIN_CLIP_DURATION, newDuration);
      
      setClipDuration(newDuration);
      
    } else if (dragHandle === 'center') {
      // Dragging center - moves the whole selection
      let newStart = dragStartValues.start + deltaTime;
      newStart = Math.max(0, Math.min(safeVidDuration - clipDuration, newStart));
      
      setClipStart(newStart);
      if (videoRef.current) videoRef.current.currentTime = newStart;
    }
  }, [videoDuration, clipDuration, clipStart, dragHandle, dragStartX, dragStartValues]);

  // Legacy scrubber handler (kept for compatibility but not used in new UI)
  const handleScrubberStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateClipPosition(e);
  };

  const updateClipPosition = useCallback((e) => {
    if (!scrubberRef.current || !videoDuration) return;
    const clientX = e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? e.clientX;
    const rect = scrubberRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const maxStart = Math.max(0, videoDuration - clipDuration);
    const newStart = percent * maxStart;
    setClipStart(newStart);
    if (videoRef.current) videoRef.current.currentTime = newStart;
  }, [videoDuration, clipDuration]);

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => { 
      e.preventDefault(); 
      if (dragHandle) {
        updateTrimPosition(e);
      } else {
        updateClipPosition(e);
      }
    };
    const end = () => {
      setIsDragging(false);
      setDragHandle(null);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
    };
  }, [isDragging, updateClipPosition, updateTrimPosition, dragHandle]);

  // Processing status message
  const [processingStatus, setProcessingStatus] = useState('');

  // Poll for job completion
  const pollJobStatus = async (jobId, maxAttempts = 30) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const response = await fetch(`${API}/gif/video-job/${jobId}`);
        if (!response.ok) throw new Error('Failed to check status');
        
        const data = await response.json();
        setProcessingStatus(data.progress || 'Processing...');
        
        if (data.status === 'completed') {
          return data;
        } else if (data.status === 'failed') {
          throw new Error(data.error || 'Processing failed');
        }
        
        // Wait 1 second before next poll
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (err) {
        if (i === maxAttempts - 1) throw err;
      }
    }
    throw new Error('Processing timeout');
  };

  // Create video clip - uses async background processing
  const createGolfShot = async () => {
    if (!videoFile) return;
    setIsCreating(true);
    setProcessingStatus('Uploading video...');

    try {
      const formData = new FormData();
      formData.append("video", videoFile);
      formData.append("start_time", clipStart.toFixed(2));
      formData.append("duration", clipDuration.toString());
      formData.append("rotation", rotation.toString());
      formData.append("filter_type", selectedLens);
      formData.append("is_round", LENS_FILTERS[selectedLens]?.isRound ? "true" : "false");
      formData.append("include_audio", includeSound ? "true" : "false");

      // Submit to async endpoint
      const response = await fetch(`${API}/gif/create-loop-async`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to start processing');

      const data = await response.json();
      const jobId = data.job_id;
      
      setProcessingStatus('Processing video...');
      
      // Poll for completion
      const result = await pollJobStatus(jobId);
      
      setOutputUrl(result.video_url);
      setStep(3);
      toast.success("Golf Shot created!");
    } catch (err) {
      toast.error(err.message || "Failed");
    } finally {
      setIsCreating(false);
      setProcessingStatus('');
    }
  };

  const handleUseInPost = async () => {
    if (!outputUrl) return;
    try {
      // Pass the video URL directly instead of converting to base64
      // This is much faster and more efficient for videos
      const fullUrl = outputUrl.startsWith('http') 
        ? outputUrl 
        : `${process.env.REACT_APP_BACKEND_URL}${outputUrl}`;
      
      onGifCreated?.(fullUrl, { 
        isRound: LENS_FILTERS[selectedLens]?.isRound || false,
        filterType: selectedLens,
        isVideo: true  // Flag to indicate this is a video URL, not base64
      }); 
      onClose();
      toast.success("Video ready for posting!");
    } catch (err) {
      console.error('handleUseInPost error:', err);
      toast.error(err.message || "Failed to prepare video for posting");
    }
  };

  // Safe calculations with NaN protection
  const safeVideoDuration = videoDuration > 0 && !isNaN(videoDuration) ? videoDuration : clipDuration;
  const clipEnd = Math.min(clipStart + clipDuration, safeVideoDuration);
  const barWidth = safeVideoDuration > 0 ? (clipDuration / Math.max(safeVideoDuration, clipDuration)) * 100 : 100;
  const barLeft = safeVideoDuration > clipDuration ? (clipStart / (safeVideoDuration - clipDuration)) * (100 - barWidth) : 0;
  const fmt = (s) => {
    if (s === null || s === undefined || isNaN(s) || !isFinite(s)) return '0.0';
    return s.toFixed(1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#111] border-gray-800 text-white w-[92vw] max-w-[340px] p-0 max-h-[85vh] overflow-hidden rounded-xl flex flex-col">
        {/* Header - Compact */}
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            {(step === 2 || step === 1.5) && !isCreating && (
              <button onClick={() => { setIsPlaying(false); setStep(1); setRecordedBlob(null); }} className="text-gray-400 hover:text-white">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <span 
              className="text-xs font-bold px-1 py-0.5 rounded"
              style={{
                color: '#22c55e',
                background: 'rgba(34, 197, 94, 0.15)'
              }}
            >
              PS
            </span>
            <span className="font-semibold text-sm">
              {step === 1 && "Golf Shots"}
              {step === 1.5 && "Review"}
              {step === 2 && (isCreating ? "Creating..." : "Select Clip")}
              {step === 3 && "Ready!"}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-2 overflow-y-auto flex-1 pb-safe" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
          {/* Step 1: Choose Photo or Video */}
          {step === 1 && (
            <div className="space-y-3">
              {!cameraPreview ? (
                /* Initial options - Filters + Photo/Video buttons */
                <>
                  {/* Camera Permission Denied Message */}
                  {cameraPermissionDenied && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <Camera className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-400 text-sm font-medium">Camera Access Required</p>
                          <p className="text-gray-400 text-xs mt-1">
                            Please allow camera access in your browser settings to take photos or videos.
                          </p>
                          <button 
                            onClick={() => {
                              setCameraPermissionDenied(false);
                              openCameraPreview();
                            }}
                            className="text-blue-400 text-xs mt-2 underline"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Filter selector - text only with colors */}
                  <div>
                    <p className="text-gray-500 text-[10px] text-center mb-2">FILTER</p>
                    <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                      {Object.entries(LENS_FILTERS).map(([key, lens]) => {
                        const isSelected = selectedLens === key;
                        return (
                          <button
                            key={key}
                            onClick={() => setSelectedLens(key)}
                            className="text-[13px] font-medium transition-all px-1"
                            style={{
                              color: lens.color,
                              opacity: isSelected ? 1 : 0.5,
                              textDecoration: isSelected ? 'underline' : 'none',
                              textUnderlineOffset: '3px',
                              transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                            }}
                          >
                            {lens.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photo and Video buttons - Text only */}
                  <div className="flex justify-center gap-8 pt-2">
                    <button 
                      onClick={() => {
                        setCaptureMode('photo');
                        openCameraPreview();
                      }} 
                      className="flex flex-col items-center gap-1.5 text-green-500 hover:text-green-400 transition-colors"
                    >
                      <Camera className="w-8 h-8" />
                      <span className="font-semibold text-sm">Photo</span>
                    </button>
                    
                    <button 
                      onClick={() => {
                        setCaptureMode('video');
                        openCameraPreview();
                      }} 
                      className="flex flex-col items-center gap-1.5 text-blue-500 hover:text-blue-400 transition-colors"
                    >
                      <Film className="w-8 h-8" />
                      <span className="font-semibold text-sm">Video</span>
                    </button>
                  </div>

                  {/* Upload option - Text only */}
                  <button 
                    onClick={() => fileInputRef.current?.click()} 
                    className="w-full flex items-center justify-center gap-2 py-3 text-gray-400 hover:text-white transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-sm">Upload from Gallery</span>
                  </button>
                  <input ref={fileInputRef} type="file" accept="video/*,image/*" onChange={handleFileUpload} className="hidden" />
                </>
              ) : (
                /* Camera preview with lens filters and capture controls */
                <div className="space-y-4">
                  {/* Loading indicator */}
                  {cameraLoading && (
                    <div className="flex items-center justify-center py-6">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                        <p className="text-gray-400 text-xs">Starting camera...</p>
                      </div>
                    </div>
                  )}

                  {/* Filter selector in camera mode - text only with colors */}
                  {!cameraLoading && (
                    <div className="flex justify-center gap-x-3 gap-y-1 flex-wrap">
                      {Object.entries(LENS_FILTERS).map(([key, lens]) => {
                        const isSelected = selectedLens === key;
                        return (
                          <button
                            key={key}
                            onClick={() => setSelectedLens(key)}
                            className="text-[12px] font-medium transition-all"
                            style={{
                              color: lens.color,
                              opacity: isSelected ? 1 : 0.5,
                              textDecoration: isSelected ? 'underline' : 'none',
                              textUnderlineOffset: '3px'
                            }}
                          >
                            {lens.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Video Preview with lens filter - ALWAYS render video element for stream attachment */}
                  <div 
                    className={`relative mx-auto bg-black flex items-center justify-center ${cameraLoading ? 'hidden' : ''}`}
                    style={{ 
                      width: selectedLens === 'fisheye' ? '200px' : '100%',
                      maxWidth: '280px',
                      height: selectedLens === 'fisheye' ? '200px' : 'auto',
                      aspectRatio: selectedLens === 'fisheye' ? '1/1' : '3/4',
                      borderRadius: selectedLens === 'fisheye' ? '50%' : '8px',
                      overflow: 'hidden',
                      boxShadow: selectedLens === 'fisheye' ? '0 0 20px rgba(59,130,246,0.3), inset 0 0 40px rgba(0,0,0,0.8)' : 'none'
                    }}
                  >
                    {/* Container for fisheye barrel distortion effect */}
                    <div 
                      className="w-full h-full overflow-hidden flex items-center justify-center"
                      style={{
                        borderRadius: selectedLens === 'fisheye' ? '50%' : '0'
                      }}
                    >
                      <video 
                        ref={videoRefCallback} 
                        autoPlay 
                        muted 
                        playsInline
                        webkit-playsinline="true"
                        className={selectedLens === 'fisheye' ? 'w-[140%] h-[140%] object-cover' : 'w-full h-full object-cover'}
                        style={{ 
                          filter: LENS_FILTERS[selectedLens]?.filter || 'none',
                          transform: isSelfieMode ? 'scaleX(-1)' : 'none'
                        }}
                      />
                    </div>
                      {/* Recording indicator */}
                      {isRecording && (
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 px-2 py-1 rounded z-10">
                          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-red-500 text-sm font-mono">{recordingTime}s</span>
                        </div>
                      )}
                      {/* Mode indicator */}
                      <div className="absolute top-3 right-3 bg-black/50 px-2 py-1 rounded text-xs text-white z-10">
                        {captureMode === 'photo' ? 'PHOTO' : 'VIDEO'}
                      </div>
                      
                      {/* Camera Flip Button - Circle arrow icon */}
                      <button
                        onClick={async () => {
                          if (isRecording) return;
                          if (streamRef.current) {
                            streamRef.current.getTracks().forEach(track => track.stop());
                          }
                          const newSelfieMode = !isSelfieMode;
                          setIsSelfieMode(newSelfieMode);
                          await new Promise(resolve => setTimeout(resolve, 100));
                          const newConstraints = { 
                            video: { 
                              facingMode: newSelfieMode ? 'user' : 'environment',
                              width: { ideal: 1280 }, 
                              height: { ideal: 720 }
                            }, 
                            audio: false 
                          };
                          try {
                            const newStream = await navigator.mediaDevices.getUserMedia(newConstraints);
                            streamRef.current = newStream;
                            if (videoRef.current) {
                              videoRef.current.srcObject = newStream;
                              await videoRef.current.play();
                            }
                            toast.success(newSelfieMode ? 'Selfie mode' : 'Back camera');
                          } catch (err) {
                            console.error('Failed to switch camera:', err);
                            toast.error('Failed to switch camera');
                          }
                        }}
                        disabled={isRecording}
                        className={`absolute bottom-3 right-3 p-3 rounded-full z-20 transition-all ${
                          isRecording 
                            ? 'bg-gray-800/50 opacity-50 cursor-not-allowed' 
                            : 'bg-black/70 hover:bg-black/90 active:scale-95'
                        }`}
                        title={isSelfieMode ? 'Switch to back camera' : 'Switch to selfie'}
                      >
                        <RefreshCcw className={`w-6 h-6 ${isSelfieMode ? 'text-blue-400' : 'text-white'}`} />
                      </button>
                      
                      {/* Selfie indicator */}
                      {isSelfieMode && (
                        <div className="absolute bottom-3 left-3 bg-blue-600/80 px-2 py-1 rounded text-xs text-white z-10">
                          SELFIE
                        </div>
                      )}
                    </div>
                  
                  {/* Capture controls - Photo or Video mode */}
                  {!cameraLoading && (
                    <div className="flex flex-col items-center justify-center py-2">
                      {captureMode === 'photo' ? (
                      /* Photo mode - single tap to capture */
                      <>
                        <p className="text-gray-400 text-[10px] mb-2">Tap to take photo</p>

                        {/* Photo capture button */}
                        <button
                          onClick={capturePhoto}
                          className="w-16 h-16 rounded-full bg-white hover:bg-gray-200 active:scale-95 transition-all flex items-center justify-center"
                          style={{ boxShadow: '0 0 15px rgba(255, 255, 255, 0.3)' }}
                        >
                          <div className="w-12 h-12 rounded-full border-4 border-green-600" />
                        </button>
                      </>
                    ) : (
                      /* Video mode - hold to record */
                      <>
                        <p className="text-gray-400 text-[10px] mb-2">
                          {isRecording ? 'Release to stop' : 'Hold to record'}
                        </p>

                        <button
                          ref={recordButtonRef}
                          onTouchStart={handleRecordTouchStart}
                          onTouchEnd={handleRecordTouchEnd}
                          onMouseDown={handleRecordMouseDown}
                          onMouseUp={handleRecordMouseUp}
                          onMouseLeave={handleRecordMouseUp}
                          className={`relative w-16 h-16 rounded-full transition-all duration-200 touch-none select-none ${
                            isRecording 
                              ? 'bg-red-600 scale-110' 
                              : 'bg-blue-600 hover:bg-blue-500 active:scale-95'
                          }`}
                          style={{
                            boxShadow: isRecording 
                              ? '0 0 25px rgba(239, 68, 68, 0.6)' 
                              : '0 0 15px rgba(59, 130, 246, 0.4)'
                          }}
                        >
                          {/* Inner circle/square indicator */}
                          <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white transition-all duration-200 ${
                            isRecording 
                              ? 'w-5 h-5 rounded-sm' 
                              : 'w-6 h-6 rounded-full'
                          }`} />
                          
                          {/* Progress ring */}
                          <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                            <circle
                              cx="32"
                              cy="32"
                              r="28"
                              fill="none"
                              stroke="rgba(255,255,255,0.3)"
                              strokeWidth="3"
                            />
                            {isRecording && (
                              <circle
                                cx="32"
                                cy="32"
                                r="28"
                                fill="none"
                                stroke="white"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeDasharray={176}
                                strokeDashoffset={176 - (recordingTime / MAX_VIDEO_SECONDS) * 176}
                                style={{ transition: 'stroke-dashoffset 0.3s linear' }}
                              />
                            )}
                          </svg>
                        </button>
                        <p className="text-gray-500 text-[10px] mt-1">Max {MAX_VIDEO_SECONDS}s</p>
                      </>
                    )}
                  </div>
                  )}
                  
                  {/* Zoom controls */}
                  {!cameraLoading && !isRecording && (
                    <div className="flex items-center justify-center gap-1 py-1">
                      {[0.5, 1, 1.5, 2].map((zoom) => (
                        <button
                          key={zoom}
                          onClick={() => applyZoom(zoom)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            zoomLevel === zoom
                              ? 'bg-yellow-500 text-black'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                          }`}
                        >
                          {zoom}x
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Cancel button */}
                  <Button 
                    onClick={cancelPreview} 
                    variant="outline" 
                    className="w-full border-gray-700 hover:bg-gray-800"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 1.5: Review - Use or Retake (handles both Photo and Video) */}
          {step === 1.5 && (
            <div className="space-y-2">
              {/* Filter indicator - at top */}
              <div className="text-center py-1">
                <span className="text-gray-400 text-sm">
                  {captureMode === 'photo' ? 'Photo' : 'Video'} with <span className="text-blue-400">{LENS_FILTERS[selectedLens]?.name || 'Standard'}</span> filter
                </span>
              </div>

              {/* Preview - Photo or Video - Constrained height */}
              {captureMode === 'photo' && capturedPhoto ? (
                /* Photo Preview - constrained height for mobile */
                <div 
                  className="relative mx-auto overflow-hidden bg-black rounded-lg flex items-center justify-center" 
                  style={{ width: '100%', maxWidth: '280px', maxHeight: '45vh' }}
                >
                  <img 
                    src={capturedPhoto}
                    alt="Captured"
                    className="w-full h-auto max-h-[45vh] object-contain"
                    style={{ 
                      filter: LENS_FILTERS[selectedLens]?.filter || 'none',
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                      display: 'block'
                    }}
                  />
                </div>
              ) : (
                /* Video Preview with rotation applied - constrained height */
                <div 
                  className="relative mx-auto overflow-hidden bg-black flex items-center justify-center rounded-lg" 
                  style={{ 
                    width: '100%',
                    maxWidth: '280px',
                    maxHeight: '40vh',
                    aspectRatio: rotation === 90 || rotation === 270 ? '16/9' : '9/16'
                  }}
                >
                  <video 
                    ref={videoRef}
                    src={videoUrl}
                    autoPlay
                    loop
                    muted 
                    playsInline
                    webkit-playsinline="true"
                    className="w-full h-full"
                    style={{ 
                      filter: LENS_FILTERS[selectedLens]?.filter || 'none',
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                      objectFit: rotation === 90 || rotation === 270 ? 'contain' : 'cover'
                    }}
                  />
                  {/* Duration badge */}
                  <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-white text-xs font-mono z-10">
                    {recordingTime}s
                  </div>
                </div>
              )}

              {/* Filter adjustment - available AFTER capture too - text only with colors */}
              <div>
                <p className="text-gray-400 text-xs text-center mb-2">Adjust filter</p>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                  {Object.entries(LENS_FILTERS).map(([key, lens]) => {
                    const isSelected = selectedLens === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedLens(key)}
                        className="text-[12px] font-medium transition-all"
                        style={{
                          color: lens.color,
                          opacity: isSelected ? 1 : 0.5,
                          textDecoration: isSelected ? 'underline' : 'none',
                          textUnderlineOffset: '3px'
                        }}
                      >
                        {lens.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Rotate button (mainly for video) */}
              {captureMode === 'video' && (
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={rotateVideo}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all"
                    title="Rotate 90°"
                  >
                    <RotateCw className="w-4 h-4" />
                    <span className="text-sm">Rotate</span>
                    {rotation > 0 && <span className="text-xs text-blue-400">({rotation}°)</span>}
                  </button>
                </div>
              )}
              
              {/* Use / Retake buttons */}
              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={() => {
                    if (captureMode === 'photo') {
                      setCapturedPhoto(null);
                      setCameraPreview(false);
                      setStep(1);
                    } else {
                      handleRetakeRecording();
                    }
                  }}
                  variant="outline" 
                  className="flex-1 border-gray-700 hover:bg-gray-800 py-3"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retake
                </Button>
                <Button 
                  onClick={() => {
                    if (captureMode === 'photo') {
                      // For photos, go directly to sharing/posting
                      if (onGifCreated && capturedPhoto) {
                        onGifCreated(capturedPhoto, 'image');
                      }
                      onClose();
                    } else {
                      handleUseRecording();
                    }
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-500 py-3"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {captureMode === 'photo' ? 'Use Photo' : 'Use Video'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select */}
          {step === 2 && !isCreating && (
            <div className="space-y-3">
              {/* MP4 quality header */}
              <div className="text-center py-1">
                <span className="text-green-400 text-xs flex items-center justify-center gap-1">
                  <span className="text-base">🎬</span>
                  HD video clips — shareable everywhere
                </span>
              </div>

              {/* Video with rotation applied */}
              <div className="relative rounded-lg overflow-hidden bg-black" onClick={togglePlay}>
                <video 
                  ref={videoRef} 
                  src={videoUrl} 
                  className="w-full aspect-video object-contain" 
                  playsInline
                  webkit-playsinline="true"
                  preload="auto"
                  muted={!includeSound}
                  onLoadedMetadata={handleVideoLoad}
                  onDurationChange={handleDurationChange}
                  onLoadedData={handleVideoLoad}
                  style={{ transform: `rotate(${rotation}deg)`, transformOrigin: 'center center' }}
                />
                {!isPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center">
                      <Play className="w-7 h-7 text-white ml-1" />
                    </div>
                  </div>
                )}
                {/* Sound indicator */}
                {includeSound && isPlaying && (
                  <div className="absolute top-2 right-2 bg-green-600/80 px-2 py-1 rounded-full flex items-center gap-1">
                    <Volume2 className="w-3 h-3 text-white" />
                    <span className="text-xs text-white">Audio</span>
                  </div>
                )}
              </div>

              {/* Rotate button only */}
              <div className="flex items-center justify-end">
                <button
                  onClick={rotateVideo}
                  className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-all"
                  title="Rotate video 90°"
                >
                  <RotateCw className="w-4 h-4" />
                  <span className="text-sm">Rotate</span>
                </button>
              </div>

              {/* Clip info */}
              <div className="flex justify-between text-sm">
                <span className="text-blue-500 font-bold">{fmt(clipDuration)}s clip</span>
                <span className="text-gray-400 font-mono">{fmt(clipStart)}s - {fmt(clipEnd)}s</span>
              </div>

              {/* Enhanced Dual-Handle Trim Slider */}
              <div 
                ref={scrubberRef} 
                className="relative h-14 bg-gray-800 rounded-lg overflow-visible select-none touch-none"
              >
                {/* Time markers */}
                <div className="absolute -top-5 inset-x-0 flex justify-between px-1">
                  {Array.from({ length: Math.min(Math.ceil(safeVideoDuration) + 1, 16) }, (_, i) => (
                    <span 
                      key={i} 
                      className="text-[10px] text-gray-500 font-mono"
                      style={{ 
                        position: 'absolute',
                        left: `${(i / safeVideoDuration) * 100}%`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      {i}s
                    </span>
                  ))}
                </div>
                
                {/* Track background */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-8 bg-gray-700 rounded-md" />
                
                {/* Selected range highlight */}
                <div 
                  className="absolute top-1/2 -translate-y-1/2 h-8 bg-blue-600/50 rounded-md"
                  style={{
                    left: `${(clipStart / safeVideoDuration) * 100}%`,
                    width: `${(clipDuration / safeVideoDuration) * 100}%`
                  }}
                />
                
                {/* Left Handle (Start) */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-6 h-12 bg-blue-500 rounded-l-lg cursor-ew-resize flex items-center justify-center shadow-lg hover:bg-blue-400 active:bg-blue-300 transition-colors z-10"
                  style={{ left: `calc(${(clipStart / safeVideoDuration) * 100}% - 12px)` }}
                  onMouseDown={(e) => handleTrimStart(e, 'left')}
                  onTouchStart={(e) => handleTrimStart(e, 'left')}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                    <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                  </div>
                </div>
                
                {/* Center drag area */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-8 cursor-grab active:cursor-grabbing flex items-center justify-center"
                  style={{
                    left: `calc(${(clipStart / safeVideoDuration) * 100}% + 14px)`,
                    width: `calc(${(clipDuration / safeVideoDuration) * 100}% - 28px)`
                  }}
                  onMouseDown={(e) => handleTrimStart(e, 'center')}
                  onTouchStart={(e) => handleTrimStart(e, 'center')}
                >
                  <div className="flex gap-1 opacity-50">
                    <div className="w-0.5 h-4 bg-white rounded-full"/>
                    <div className="w-0.5 h-4 bg-white rounded-full"/>
                    <div className="w-0.5 h-4 bg-white rounded-full"/>
                  </div>
                </div>
                
                {/* Right Handle (End) */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-6 h-12 bg-blue-500 rounded-r-lg cursor-ew-resize flex items-center justify-center shadow-lg hover:bg-blue-400 active:bg-blue-300 transition-colors z-10"
                  style={{ left: `calc(${((clipStart + clipDuration) / safeVideoDuration) * 100}% - 12px)` }}
                  onMouseDown={(e) => handleTrimStart(e, 'right')}
                  onTouchStart={(e) => handleTrimStart(e, 'right')}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                    <div className="w-0.5 h-4 bg-white/70 rounded-full" />
                  </div>
                </div>
              </div>
              
              <p className="text-gray-500 text-xs text-center flex items-center justify-center gap-1">
                <span className="text-blue-400">⟵</span>
                Drag handles to trim
                <span className="text-blue-400">⟶</span>
              </p>

              {/* Filter buttons - text only with colors */}
              <div className="space-y-2">
                <p className="text-gray-400 text-xs text-center">Filter</p>
                <div className="flex justify-center gap-x-3 gap-y-1 flex-wrap">
                  {Object.entries(LENS_FILTERS).map(([key, filter]) => {
                    const isSelected = selectedLens === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedLens(key)}
                        className="text-[12px] font-medium transition-all"
                        style={{
                          color: filter.color,
                          opacity: isSelected ? 1 : 0.5,
                          textDecoration: isSelected ? 'underline' : 'none',
                          textUnderlineOffset: '3px'
                        }}
                      >
                        {filter.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sound toggle */}
              <div className="flex items-center justify-center gap-3 py-2">
                <button
                  onClick={() => setIncludeSound(!includeSound)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    includeSound 
                      ? 'bg-green-600/20 text-green-400 border border-green-500/50' 
                      : 'bg-gray-800 text-gray-500 border border-gray-700'
                  }`}
                >
                  {includeSound ? (
                    <>
                      <Volume2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Sound ON</span>
                    </>
                  ) : (
                    <>
                      <VolumeX className="w-4 h-4" />
                      <span className="text-sm font-medium">Sound OFF</span>
                    </>
                  )}
                </button>
              </div>

              <Button onClick={createGolfShot} className="w-full bg-blue-600 hover:bg-blue-500 py-3 text-lg font-bold">
                <Scissors className="w-5 h-5 mr-2" />Create Golf Shot
              </Button>
            </div>
          )}

          {/* Creating */}
          {step === 2 && isCreating && (
            <div className="text-center py-16">
              <Loader2 className="w-14 h-14 mx-auto text-green-500 animate-spin mb-4" />
              <p className="text-lg">Creating Golf Shot...</p>
              <p className="text-gray-400 text-sm mt-2">{processingStatus || 'Just a moment'}</p>
              <div className="mt-4 w-48 mx-auto bg-gray-800 rounded-full h-1.5">
                <div className="bg-green-500 h-1.5 rounded-full animate-pulse" style={{width: '60%'}}></div>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && outputUrl && (
            <div className="space-y-4">
              {/* Round display for fisheye, regular for others */}
              <div 
                className={`overflow-hidden bg-black flex items-center justify-center mx-auto ${
                  LENS_FILTERS[selectedLens]?.isRound ? 'rounded-full w-72 h-72' : 'rounded-lg'
                }`}
                style={LENS_FILTERS[selectedLens]?.isRound ? {
                  boxShadow: '0 0 30px rgba(59,130,246,0.3)'
                } : {}}
              >
                <video 
                  src={outputUrl.startsWith('http') ? outputUrl : `${process.env.REACT_APP_BACKEND_URL}${outputUrl}`} 
                  autoPlay
                  loop
                  muted={!includeSound}
                  playsInline
                  controls={includeSound}
                  onError={(e) => {
                    console.error('Video load error:', e);
                    // Try adding a cache-busting query param
                    const currentSrc = e.target.src;
                    if (!currentSrc.includes('?t=')) {
                      e.target.src = currentSrc + '?t=' + Date.now();
                    }
                  }}
                  className={LENS_FILTERS[selectedLens]?.isRound 
                    ? "w-full h-full object-cover rounded-full" 
                    : "max-w-full max-h-[60vh] object-contain"
                  }
                />
              </div>
              {LENS_FILTERS[selectedLens]?.isRound && (
                <p className="text-center text-xs text-blue-400">Fisheye • Round output</p>
              )}
              {includeSound && (
                <p className="text-center text-xs text-green-400 flex items-center justify-center gap-1">
                  <Volume2 className="w-3 h-3" /> With sound
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={resetAll} variant="outline" className="border-gray-700 py-3">
                  <RefreshCw className="w-4 h-4 mr-2"/>Retake
                </Button>
                <Button onClick={handleUseInPost} className="bg-green-600 hover:bg-green-500 py-3">
                  <CheckCircle className="w-4 h-4 mr-2"/>Post
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GolfShots = ({ onBack }) => {
  const [show, setShow] = useState(true);
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <GolfShotsModal isOpen={show} onClose={() => onBack?.() || setShow(false)} onGifCreated={() => toast.success("Ready!")} />
      {!show && <Button onClick={() => setShow(true)} className="bg-green-600">Open Golf Shots</Button>}
    </div>
  );
};

export default GolfShots;
export { GolfShotsModal };
